import React, { FC, ReactNode } from 'react';
import { BodyText, ButtonAsWrapper, FlexCol, FlexRow, PaywallButton, TextDynamic } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { HEADSHOTS_MODAL_ACTION_PROPS, HeadshotsModalLayout } from './HeadshotsModalLayout';

export type HeadshotsInfoBenefit = {
  id: string;
  label: ReactNode;
};

export type HeadshotsInfoModalProps = {
  title: string;
  body: ReactNode;
  benefits: HeadshotsInfoBenefit[];
  prompt?: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel: string;
  onSecondaryAction: () => void;
  closeLabel: string;
  onClose: () => void;
};

/**
 * Shared headshots upsell modal: plan benefits, a gold upgrade action, and a
 * dismiss link. Copy and actions are props so the trial and free upsells reuse it.
 */
export const HeadshotsInfoModal: FC<HeadshotsInfoModalProps> = ({
  title,
  body,
  benefits,
  prompt,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  closeLabel,
  onClose,
}) => (
  <HeadshotsModalLayout
    title={title}
    summary={<BodyText>{body}</BodyText>}
    details={
      <>
        <FlexCol gap="2" w="full" grow={0}>
          {benefits.map((benefit) => (
            <FlexRow key={benefit.id} gap="2" alignItems="center" wrap="nowrap">
              <Icon name="checkmark" size="md" themeColor="success" />
              <TextDynamic fontSize="bodyRegular" mdProps={{ fontSize: 'bodyLarge' }}>
                {benefit.label}
              </TextDynamic>
            </FlexRow>
          ))}
        </FlexCol>
        {prompt && <BodyText>{prompt}</BodyText>}
      </>
    }
    actions={
      <>
        <PaywallButton
          color="gold"
          {...HEADSHOTS_MODAL_ACTION_PROPS}
          label={primaryActionLabel}
          onClick={onPrimaryAction}
        />
        <ButtonAsWrapper
          aria-label={secondaryActionLabel}
          onClick={onSecondaryAction}
          fullWidth
          noPadding
          data-testid="headshots-info-modal-secondary-action"
        >
          <TextDynamic
            as="span"
            fontWeight="medium"
            align="center"
            fontSize="bodyRegular"
            mdProps={{ fontSize: 'bodyLarge', fontWeight: 'medium' }}
          >
            {secondaryActionLabel}
          </TextDynamic>
        </ButtonAsWrapper>
      </>
    }
    closeLabel={closeLabel}
    onClose={onClose}
  />
);

export default HeadshotsInfoModal;
